import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createSearchIndex, searchClients, processClientData } from '../utils/clientSearch';
import { applyFilters } from '../utils/clientFilters';
import { getPaginatedData, getTotalPages } from '../utils/clientPagination';
import { useApp } from './AppContext';
import { API_CONFIG, getApiUrl } from '../config/api';
import { useSalon } from './SalonContext';
import { removeNullValues } from '../utils/apiUtils';

const ClientContext = createContext();

export function ClientProvider({ children }) {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    isOptedInReminder: false,
    isOptedInMarketing: false
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState({
    field: 'last_name',
    direction: 'asc'
  });

  // Create search index when clients change
  const searchIndex = useMemo(() => {
    return createSearchIndex(clients);
  }, [clients]);

  // Process clients based on search, filters, and sorting
  const processedClients = useMemo(() => {
    return processClientData({
      clients,
      searchIndex,
      searchQuery,
      filters,
      sorting
    });
  }, [clients, searchIndex, searchQuery, filters, sorting]);

  // Get paginated data
  const paginatedClients = useMemo(() => {
    return getPaginatedData(processedClients, page, pageSize);
  }, [processedClients, page, pageSize]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return getTotalPages(processedClients.length, pageSize);
  }, [processedClients.length, pageSize]);

  const fetchClients = async () => {
    if (!businessInfo?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_CLIENTS, '', { salonId: businessInfo.id })
      );
      
      if (!response?.clients) {
        throw new Error('Invalid response format from server');
      }
      
      setClients(response.clients);
    } catch (err) {
      setError(err.message || 'Failed to fetch clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (clientData) => {
    if (!businessInfo?.id) {
      throw new Error('No salon ID available');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS),
        {
          method: 'POST',
          body: JSON.stringify(removeNullValues({
            ...clientData,
            salon_uuid: businessInfo.id
          }))
        }
      );

      // Add the new client to the list
      setClients(prevClients => [...prevClients, response.client]);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to create client');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (clientId, clientData) => {
    if (!businessInfo?.id) {
      throw new Error('No salon ID available');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, '', { clientId }),
        {
          method: 'PUT',
          body: JSON.stringify(clientData)
        }
      );

      // Update the client in the list
      setClients(prevClients => 
        prevClients.map(client => {
          if (client.id === clientId) {
            return {
              ...client,
              ...response.client
            };
          }
          return client;
        })
      );
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update client');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!businessInfo?.id) {
      throw new Error('No salon ID available');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, '', { clientId }),
        {
          method: 'PUT',
          body: JSON.stringify({status: 'Inactive'})
        }
      );

      // Remove the client from the list
      setClients(prevClients => 
        prevClients.filter(client => client.id !== clientId)
      );
      return response;
    } catch (err) {
      setError(err.message || 'Failed to delete client');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessInfo?.id) {
      fetchClients();
    } else {
      setClients([]);
    }
  }, [businessInfo?.id]);

  const value = {
    clients: paginatedClients,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    sorting,
    setSorting,
    refreshClients: fetchClients,
    createClient,
    updateClient,
    deleteClient
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
} 